import readyState from 'ready-state';
import { isCatalogItem, isHtmlElement } from '../utilities/guards';
import Logger from '../utilities/logger';
import { toKebabCase } from '../utilities/string';
import { getParsedHydrationData } from './dataHydration';
import Injector, { isInjectableKey } from './injector';
export var DataAttributes;
(function (DataAttributes) {
    DataAttributes["CatalogItem"] = "catalogItem";
})(DataAttributes || (DataAttributes = {}));
const instantiationByMutationCatalogItems = new Set();
const instantiationByDomCatalogItems = new Set();
const instantiationByHydrationDataCatalogItems = new Set();
const instantiatedIds = new Set();
export const addModuleForInstantiationFromHydrationData = (moduleName) => {
    instantiationByHydrationDataCatalogItems.add(moduleName);
};
export const addModuleForInstantiationFromDom = (moduleName) => {
    instantiationByDomCatalogItems.add(moduleName);
};
export const addModuleForInstantiationFromMutation = (moduleName) => {
    instantiationByMutationCatalogItems.add(moduleName);
};
const instantiateModuleFromHydrationData = (moduleName) => {
    const data = getParsedHydrationData(moduleName);
    // Loop through the data and instantiate an object for each data item in the array
    data.forEach((options) => {
        // Prevent instantiating the multiple element twice by checking for ID
        if ('id' in options) {
            if (!instantiatedIds.has(options.id)) {
                // Instantiate the object through DI
                Injector.resolve(moduleName, options);
                instantiatedIds.add(options.id);
            }
        }
        else {
            Injector.resolve(moduleName, options);
        }
    });
};
/**
 * Loops through an array of hydration data and hydrates everything through the Injector
 * @param key Module name to hydrate
 * @param data Hydration Data
 */
// export function instantiateFromHydrationData(key: unknown, data: DependencyParameters[]): void {
export const instantiateFromHydrationData = (moduleName) => {
    if (isInjectableKey(moduleName)) {
        instantiateModuleFromHydrationData(moduleName);
    }
    else if (!moduleName) {
        instantiationByHydrationDataCatalogItems.forEach(instantiateModuleFromHydrationData);
    }
};
/**
 * Using the 'data-catalogItem="{ComponentName"}' data attribute, initializes components based
 * on the component name provided.
 *
 * @param elements Collection of HTML elements to create component instances for
 */
export const instantiateFromDataAttributes = (elements) => {
    elements.forEach((element) => {
        // If the data attribute is present
        if (element.dataset[DataAttributes.CatalogItem]) {
            // Fetch the proper constructor from the injector
            const component = Injector.getConstructor(element.dataset[DataAttributes.CatalogItem]);
            if (typeof component === 'undefined') {
                Logger.error(`Attempt to hydrate unknown catalog item '${component}' from data attributes`);
            }
            else if (isCatalogItem(component)) {
                component.createFromDataAttributes(element);
            }
        }
    });
};
const instantiateModuleFromDom = (moduleName, domRoot = document) => {
    // Fetch the DOM elements with the provided key
    const elements = domRoot.querySelectorAll(`[data-${toKebabCase(DataAttributes.CatalogItem)}="${moduleName}"]`);
    // Instantiate the components
    instantiateFromDataAttributes(elements);
};
/**
 * Loops through an array of modules slated for instantiation from DOM
 * @param key Module name to hydrate
 */
export const instantiateFromDom = (moduleName, domRoot) => {
    if (isInjectableKey(moduleName)) {
        instantiateModuleFromDom(moduleName, domRoot);
    }
    else if (!moduleName) {
        instantiationByDomCatalogItems.forEach((value) => {
            instantiateModuleFromDom(value, domRoot);
        });
    }
};
/**
 * ONLY EXPORTED FOR TESTING PURPOSES
 * @private
 */
export const mutationObserverCallback = (mutationsList) => {
    // Elements that will need instantiation
    const elements = [];
    // Loop through the provided mutations and check for matches
    mutationsList.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
            if (!isHtmlElement(node)) {
                return;
            }
            // Check if the 'data-catalogItem' attribute exists and if it matches
            const catalogItemName = node.dataset[DataAttributes.CatalogItem];
            if (isInjectableKey(catalogItemName)) {
                if (instantiationByMutationCatalogItems.has(catalogItemName)) {
                    elements.push(node);
                }
            }
            const children = node.querySelectorAll(`[data-${DataAttributes.CatalogItem}]`);
            children.forEach((childNode) => {
                const child = childNode;
                const childCatalogItemName = child.dataset[DataAttributes.CatalogItem];
                if (isInjectableKey(childCatalogItemName)) {
                    if (instantiationByMutationCatalogItems.has(childCatalogItemName)) {
                        elements.push(child);
                    }
                }
            });
        });
    });
    // If any elements exist for instantiation, then instantiate them
    if (elements.length) {
        instantiateFromDataAttributes(elements);
    }
};
export const startObservingMutations = () => {
    // Define a mutation observer to watch for elements being added
    const observer = new MutationObserver(mutationObserverCallback);
    // Register the observer on the entire document
    observer.observe(document, {
        subtree: true,
        childList: true,
    });
};
export const clearRegisteredCatalogItems = () => {
    instantiationByHydrationDataCatalogItems.clear();
    instantiationByMutationCatalogItems.clear();
    instantiatedIds.clear();
};
readyState.domready.then(() => {
    startObservingMutations();
    instantiateFromHydrationData();
    instantiateFromDom();
});
